import React, { Fragment, useState, useEffect } from "react";
import {
  Row,
  Table,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Input,
  Label,
  Container,
} from "reactstrap";
import GenericButton from "../UI/Button/Button";
import { useSelector } from "react-redux";
import { TitleLabel } from "../UI/TitleLabel/TitleLabel";

import {
  fetchGetPortfolioNoLogs,
  fetchGetActivity,
  fetchGetActivityProduct,
  fetchProductActivityPDF,
  getPortfolioXLS,
} from "../../services/api/accounts";

import { RadioButton, RadioButtonGroup } from "../UI/RadioButton/RadioButton";
import HistoryTable from "./HistoryTable";
import { getHistoryXLS, getHistoryCSV } from "../../services/api/accounts";
import { getCurrentAccount, getTexts } from "../../reducers/selectors";
import { showToast } from "../../utils/helper";
import { FormattedNumber } from "../FormattedNumber/FormattedNumber";
import moment from "moment";
import HistoryChart from "./HistoryChart";
import { toPascalCase } from "../../utils/helper";
import { Warning } from "../Warning/Warning";
import pdf from "../../../public/svg/pdf.svg";
import excel from "../../../public/svg/xlsx.svg";
import csv from "../../../public/svg/csv.svg";
import arrow from "../../../public/svg/keyboard_arrow_down.svg";

import style from "./HistoryForm.scss";
import HistoryModal from "./HistorModal";
import { CircularProgress } from "../UI/CircularProgress/CircularProgress";

export const HistoryForm = () => {
  const [loadingPosition, setLoadingPosition] = useState(true);
  const [portfolioNoLogs, setPortfolioNoLogs] = useState([]);
  const [activity, setActivity] = useState();
  const [modal, setModal] = useState(false);

  const currentAccount = useSelector(getCurrentAccount);
  const texts = useSelector(getTexts);
  const [userDropdownIsOpen, setUserDropdownIsOpen] = useState(false);

  const todayDate = new Date().toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(todayDate);
  const [toDate, setToDate] = useState(todayDate);
  const [currencyFilter, setCurrency] = useState({ id: 0, instrumento: "", ISO: "" });

  const [displayDate, setDisplayDate] = useState(false);
  const [tableFilter, setTableFilter] = useState();
  const [chartData, setChartData] = useState({ xData: [], yData: [] });

  const [currentFilter, setCurrentFilter] = useState("30");
  const [saldo, setSaldo] = useState({ saldoInicial: "", saldoFinal: "", difSigno: "", diferencia: "" });

  useEffect(() => {
    if (currentAccount.AccountNumber) {
      fetchGetPortfolioNoLogs(currentAccount.AccountNumber)
        .then(({ response }) => {
          const valores = [];
          response.position.cash.reverse().forEach((moneda) =>
            valores.push({
              id: moneda.currencyId,
              instrumento: moneda.currencyName,
              esEfectivo: true,
              ISO: moneda.currencyISO,
            })
          );

          response.position.asset.forEach((asset) =>
            valores.push({
              id: asset.id,
              instrumento: asset.name,
              esEfectivo: false,
              ISO: "",
            })
          );
          setPortfolioNoLogs(valores);
          if (valores.length > 0) setCurrency(valores[0]);
        })
        .catch((err) => {
          showToast("Hubo un error obteniendo datos del servidor", true);
          console.error(err);
        })
        .finally(() => setLoadingPosition(false));
    }
  }, [currentAccount]);

  useEffect(() => {
    if (activity) {
      setChartData({ yData: [], xData: [] });
      const saldoInicialEncontrado = activity.find((item) => item.concepto === "Saldo Inicial").saldo_total;

      const saldoFinalEncontrado = activity.find((item) => item.concepto === "Saldo Final").saldo_total;
      const diferencia = saldoInicialEncontrado
        ? ((saldoFinalEncontrado - saldoInicialEncontrado) * 100) / saldoInicialEncontrado
        : saldoFinalEncontrado * 100;

      setSaldo({
        ...saldo,
        saldoInicial: saldoInicialEncontrado || 0,
        saldoFinal: saldoFinalEncontrado || 0,
        diferencia: diferencia || 0,
        difSigno: diferencia < 0 ? "-" : "+",
      });
      const fechasExtraidas = activity.map((item) => item.fecha);
      const saldosExtraidos = activity.map((item) => item.acumulado);
      setChartData({ yData: saldosExtraidos, xData: fechasExtraidas });
    }
  }, [activity]);

  useEffect(() => {
    if (tableFilter) updateActivity();
  }, [tableFilter, currentAccount, currencyFilter]);

  useEffect(() => {
    handleChangeFilter(currentFilter);
  }, [currentFilter]);

  const toggleUserDropdown = () => {
    setUserDropdownIsOpen((prevState) => !prevState);
  };

  const toggleFilterType = (filterType) => {
    setCurrentFilter(filterType);
  };

  const handleChangeFilter = (filterType) => {
    const newFilter = {
      desde: moment().subtract(1, "days").valueOf(),
    };

    switch (filterType) {
      case "30":
        setDisplayDate(false);
        newFilter.desde = moment().subtract(30, "days").valueOf();
        break;
      case "7":
        setDisplayDate(false);
        newFilter.desde = moment().subtract(7, "days").valueOf();
        break;
      case "24":
        setDisplayDate(false);
        newFilter.desde = moment().subtract(24, "hours").valueOf();
        break;
      case "10":
        setDisplayDate(false);
        newFilter.desde = moment().subtract(10, "days").valueOf();
        break;
      case "actual":
        setDisplayDate(false);
        newFilter.desde = moment().date(1).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf();
        break;
      case "anterior":
        setDisplayDate(false);
        newFilter.desde = moment()
          .subtract(1, "month")
          .date(1)
          .hours(0)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .valueOf();
        newFilter.hasta = moment()
          .date(1)
          .subtract(1, "days")
          .hours(0)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .valueOf();
        break;
      case "date":
        newFilter.desde = moment(fromDate).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf();
        newFilter.hasta = moment(toDate).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf();
        break;
      case "currency":
        setDisplayDate(false);
        newFilter.currency = currencyFilter.id;
        break;
    }

    setTableFilter(newFilter);
  };

  const updateActivity = () => {
    if (currentAccount.AccountNumber && currencyFilter.id !== 0) {
      // controlar que sea máximo 3 meses
      const timeSpan = moment(tableFilter.desde).diff(moment(tableFilter.hasta));
      var d = moment.duration(timeSpan);
      if (d._data.months <= -3) showToast("Rango de fechas inválido", true);
      else {
        if (!loadingPosition) setLoadingPosition(() => true);
        const getActivity = currencyFilter.esEfectivo ? fetchGetActivity : fetchGetActivityProduct;
        getActivity(currentAccount.AccountNumber, currencyFilter.id, tableFilter)
          .then(({ response }) => {
            const newActivity = [];
            let actividad = {
              nombre: "",
              concepto: "Saldo Inicial",
              fecha: moment(tableFilter.desde).format("DD-MM-YY"),
              saldo_total: parseFloat(response.activity[0].balance),
              acumulado: parseFloat(response.activity[0].balance),
              totales: [],
            };
            response.activity[0].acumulado = actividad.acumulado;
            newActivity.push(actividad);
            var finalBalance = parseFloat(response.activity[0].balance);
            response.activity.forEach(function (it, index) {
              if (index > 0) {
                actividad = {
                  nombre: it.description,
                  concepto: it.concept,
                  fecha: moment(it.date).format("DD-MM-YY"),
                  saldo_total: parseFloat(it.credit) - parseFloat(it.debit),
                  acumulado:
                    parseFloat(response.activity[index - 1].acumulado) +
                    (parseFloat(it.credit) - parseFloat(it.debit)),
                  activityId: it.activityId,
                  journalEntryId: it.journalEntryId,
                  totales: [
                    {
                      detalle: "Balance",
                      valor: parseFloat(it.credit) - parseFloat(it.debit),
                    },
                  ],
                  currencyISO: it.CurrencyISO,
                };
                newActivity.push(actividad);
                it.acumulado = actividad.acumulado;
              }
              finalBalance += parseFloat(it.credit) - parseFloat(it.debit);
            });
            actividad = {
              nombre: "",
              concepto: "Saldo Final",
              fecha: moment(tableFilter.hasta).format("DD-MM-YY"),
              saldo_total: finalBalance,
              acumulado: finalBalance,
              totales: [],
            };
            newActivity.push(actividad);

            setActivity(newActivity);
          })
          .catch((err) => {
            showToast("Hubo un error obteniendo datos históricos del servidor", true);
            console.error(err);
          })
          .finally(() => setLoadingPosition(false));
      }
    }
  };

  const changeDateHandler = (event) => {
    event.target.id === "fromDate" ? setFromDate(event.target.value) : null;
    event.target.id === "toDate" ? setToDate(event.target.value) : null;
  };

  const onSearch = () => {
    const newFilter = {
      desde: moment(fromDate).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf(),
      hasta: moment(toDate).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf(),
    };
    setTableFilter(newFilter);
  };

  const handleXLS = () => {
    getHistoryXLS(activity).then((r) => {
      try {
        const blob = new Blob([new Uint8Array(r.response.buffer.data)], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "History.xlsx";
        link.click();
      } catch (ex) {
        console.error(ex);
        showToast("Error obteniendo archivo", true);
      }
    });
  };

  const getActivityPDF = (activityItem) => {
    fetchProductActivityPDF({
      activityId: activityItem.activityId,
      journalEntryId: activityItem.journalEntryId,
    })
      .then(({ response }) => {
        if (response.result.Code !== 200) {
          showToast("Error obteniendo pdf del servidor", true);
        } else {
          var a = document.createElement("A");
          a.href = `data:application/octet-stream;base64,${response.blobfile}`;
          a.download = `${moment(activityItem.fecha).format("DD-MM-YY")}-${activityItem.journalEntryId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      })
      .catch((err) => console.error(err));
  };

  if (currentAccount.noAccounts)
    return (
      <Row>
        <Col>
          <span>No hay cuentas asociadas</span>
        </Col>
      </Row>
    );

  return (
    <Row>
      <Row className={style.headerTitle}>
        <Label>Histórico</Label>
      </Row>
      <Row className={style.rowContainer} style={{ margin: "12px", width: "100%" }}>
        <Col style={{ width: "100%" }}>
          <Container className={style.history}>
            <Row style={{ width: "100%" }}>
              <Col>
                <TitleLabel className={style.TitleLabel} label="Selecciona el tipo de activo" />
              </Col>
              <Col className={style.colEnd}>
                <Dropdown isOpen={userDropdownIsOpen} toggle={toggleUserDropdown} direction={"down"}>
                  <DropdownToggle className={style.dropdownToggle}>
                    <span>{currencyFilter.instrumento}</span>
                    <img src={arrow} />
                  </DropdownToggle>
                  <DropdownMenu>
                    {portfolioNoLogs.map((itemCuentas) => {
                      return (
                        <DropdownItem
                          className={`${style.greyFont} ${style.activeItem}`}
                          onClick={() => {
                            setCurrency(itemCuentas);
                          }}
                          tag="span"
                        >
                          {itemCuentas.instrumento}
                        </DropdownItem>
                      );
                    })}
                  </DropdownMenu>
                </Dropdown>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
      <Row className={style.rowContainerSecond} style={{ width: "100%" }}>
        <Col style={{ width: "100%" }}>
          <Container className={style.history}>
            <Row style={{ width: "100%" }}>
              <Col className={style.radioBut}>
                <RadioButtonGroup>
                  <RadioButton
                    label="30 dias"
                    onClick={() => {
                      toggleFilterType("30");
                    }}
                    active={currentFilter === "30"}
                  />
                  <RadioButton
                    label="7 dias"
                    onClick={() => {
                      toggleFilterType("7");
                    }}
                    active={currentFilter === "7"}
                  />
                  <RadioButton
                    label="24 horas"
                    onClick={() => {
                      toggleFilterType("24");
                    }}
                    active={currentFilter === "24"}
                  />
                </RadioButtonGroup>
              </Col>
              <div className={style.buttonContainer}>
                <Col
                  className={style.calendarButton}
                  active={currentFilter === "date"}
                  onClick={() => {
                    toggleFilterType("date");
                    setModal(!modal);
                    setDisplayDate(true);
                  }}
                >
                  <img src="../../svg/ButtonIcons/calendar.svg"></img>
                  <div>
                    <label className={style.selectDates}>Seleccionar fechas</label>
                  </div>
                </Col>
                <HistoryModal
                  fromDate={fromDate}
                  changeDateHandler={changeDateHandler}
                  toDate={toDate}
                  displayDate={displayDate}
                  onSearch={onSearch}
                  modal={modal}
                  setModal={setModal}
                />
                <Col className={style.exportButton}>
                  <img src="../../svg/ButtonIcons/export.svg" onClick={handleXLS}></img>
                </Col>
              </div>
            </Row>

            <Row style={{ width: "100%" }}>
              <Col className={style.charContainer}>
                {chartData.yData.length === 0 ? (
                  <CircularProgress />
                ) : (
                  <HistoryChart yData={chartData.yData} xData={chartData.xData} />
                )}
              </Col>
              <Col
                style={{
                  width: "fit-content",
                  maxWidth: "fit-content",
                  margin: " 50px 100px 30px 44px",
                }}
                className={style.resumeInfo}
              >
                <BalanceContainer
                  title={"Saldo inicial"}
                  price={saldo.saldoInicial}
                  decimals={currencyFilter.ISO === "PYG" ? 0 : 2}
                />
                <BalanceContainer
                  title={"Saldo final"}
                  price={saldo.saldoFinal}
                  decimals={currencyFilter.ISO === "PYG" ? 0 : 2}
                />
                <BalanceContainerDiference
                  title={"Diferencia"}
                  //price={saldo.difSigno + parseFloat(saldo.diferencia).toFixed(2) + "%"}
                  price={parseFloat((100 * (saldo.saldoFinal - saldo.saldoInicial)) / saldo.saldoFinal)}
                />
              </Col>
            </Row>
            <Row style={{ width: "100%", margin: "0px" }}>
              <Col>
                <div className={style.tableTitle}>Últimos movimientos</div>
                <HistoryTable
                  activity={activity}
                  toPascalCase={toPascalCase}
                  getActivityPDF={getActivityPDF}
                  pdf={pdf}
                  currencyFilter={currencyFilter}
                ></HistoryTable>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </Row>
  );
};
const BalanceContainer = ({ title, price, decimals = 2 }) => {
  return (
    <React.Fragment>
      <Row className={style.rowBalance}>
        <Col>
          <Label className={style.balanceTitle}>{title}</Label>
        </Col>
        <Col>
          <Label className={style.balancePrice}>
            <FormattedNumber
              value={price}
              minimumFractionDigits={decimals}
              maximumFractionDigits={decimals}
            />
          </Label>
        </Col>
      </Row>
    </React.Fragment>
  );
};

const BalanceContainerDiference = ({ title, price }) => {
  return (
    <React.Fragment>
      <Row className={style.rowDiference}>
        <Col>
          <Label className={style.balanceTitle}>{title}</Label>
        </Col>
        <Col>
          <Label className={style.balancePrice}>
            <FormattedNumber value={price} minimumFractionDigits={2} maximumFractionDigits={2} />%
          </Label>
        </Col>
      </Row>
    </React.Fragment>
  );
};
